/**
 * Window presets, in Hounsfield Units.
 *
 * **CT ONLY, and the reason is the same one that kept the ingest path's
 * baked windows to CT.** Hounsfield is absolute -- 40/400 is soft tissue
 * on every CT ever made -- so a preset is a real instruction. MR, CR and
 * DX store values with no absolute meaning, where "brain" would be a
 * number applied to arbitrary units and would land somewhere different
 * on every scanner. Those modalities get the file's own window and the
 * right-drag, which is what a radiologist uses on them anyway.
 *
 * The numbers are the ordinary clinical ones. They are not tuned here
 * and should not be: a radiologist reaching for "3" expects the bone
 * window they have used for twenty years, not ours.
 */

export interface WindowPreset {
  /** The key that selects it. Number keys, in the conventional order. */
  key: string;
  label: string;
  /** Window width, then centre. */
  width: number;
  centre: number;
}

export const CT_PRESETS: readonly WindowPreset[] = [
  { key: "1", label: "Soft tissue", width: 400, centre: 40 },
  { key: "2", label: "Lung", width: 1500, centre: -600 },
  { key: "3", label: "Bone", width: 2000, centre: 300 },
  { key: "4", label: "Brain", width: 80, centre: 40 },
  { key: "5", label: "Liver", width: 150, centre: 60 },
  { key: "6", label: "Angio", width: 600, centre: 300 },
];

export function presetsFor(modality: string | null | undefined): readonly WindowPreset[] {
  return String(modality || "").toUpperCase() === "CT" ? CT_PRESETS : [];
}

/**
 * A window as Cornerstone wants it.
 *
 * Cornerstone takes a RANGE rather than a width and a centre, and the
 * conversion is the one place a sign error turns a lung window into a
 * bone window silently.
 */
export function rangeOf(preset: { width: number; centre: number }): {
  lower: number;
  upper: number;
} {
  return {
    lower: preset.centre - preset.width / 2,
    upper: preset.centre + preset.width / 2,
  };
}

/** The inverse, for the readout. A radiologist reads W/L, not a range. */
export function widthCentreOf(range: { lower: number; upper: number }): {
  width: number;
  centre: number;
} {
  return {
    width: Math.round(range.upper - range.lower),
    centre: Math.round((range.upper + range.lower) / 2),
  };
}

/**
 * The reading room's presets -- the design's own values and keys
 * (docs/design/PACS Viewer Redesign-31.html, the CT screen's `presets`).
 *
 * NOT `CT_PRESETS` with new keys, and the difference is deliberate:
 * the design puts bone at 2000/400 where the existing viewer has
 * 2000/300, drops Angio, and moves the keys to F1-F5 because it gives
 * the number keys to layouts. The existing viewer keeps its own set
 * where it still renders; see docs/scope-reading-room.md section 3.
 */
export interface RoomPreset extends WindowPreset {
  /** The toolbar's one-word label. */
  short: string;
}

export const ROOM_CT_PRESETS: readonly RoomPreset[] = [
  { key: "F1", label: "Soft tissue", short: "Soft", width: 400, centre: 40 },
  { key: "F2", label: "Lung", short: "Lung", width: 1500, centre: -600 },
  { key: "F3", label: "Bone", short: "Bone", width: 2000, centre: 400 },
  { key: "F4", label: "Brain", short: "Brain", width: 80, centre: 40 },
  { key: "F5", label: "Liver", short: "Liver", width: 150, centre: 60 },
];

export function roomPresetsFor(modality: string | null | undefined): readonly RoomPreset[] {
  return String(modality || "").toUpperCase() === "CT" ? ROOM_CT_PRESETS : [];
}
